<template>
  <!--
    Um ícone de arte (o peixe do catálogo, a vara, o mapa) é `<img>`; um ícone de INTERFACE é um
    `<i>` com classe `fa-*` que o `pixel-icons.css` transforma em sprite. Os dois vivem aqui porque
    quem usa não deveria precisar saber qual é qual — mas a regra do D20 continua valendo por baixo:
    sprite para UI, arte do Fishbay para conteúdo.
  -->
  <img
    v-if="src"
    class="fish-icon fish-icon--arte"
    :class="[`fish-icon--${tamanho}`, { 'fish-icon--silhueta': silhueta }]"
    :src="src"
    :alt="silhueta ? rotuloDaSilhueta : alt"
    loading="lazy"
    decoding="async"
    @error="aoFalhar"
  />

  <i
    v-else
    class="fish-icon"
    :class="[`fa-${nome}`, `fish-icon--${tamanho}`]"
    :aria-hidden="alt ? null : 'true'"
    :aria-label="alt || null"
    :role="alt ? 'img' : null"
  />
</template>

<script>
/**
 * 2.4 — `FishIcon`. **Sprite, nunca emoji** (UI-10, D20).
 *
 * ## O maior contraste isolado do cliente
 *
 * Havia **1.059 emojis escritos direto nos templates**, com o sistema de sprites pixel pronto e
 * ocioso ao lado. E emoji não é um caractere neutro: ele renderiza com a fonte do sistema
 * operacional — no Windows, Segoe UI Emoji, que é **colorida, arredondada e vetorial**. Ao lado de
 * pixel art de 32×32, é a maior distância visual que existia entre a arte do jogo e a interface.
 *
 * ## A silhueta (D14, D21)
 *
 * > Peixe não descoberto aparece como **silhueta**, nunca como espaço vazio. É o padrão do Tibia e
 * > de toda Pokédex — a silhueta cria curiosidade, o vazio não cria nada.
 *
 * **Este é o único lugar do redesenho em que um `filter` CSS em runtime é a resposta certa**, e vale
 * dizer por quê: a arte do peixe vem do catálogo **pela API**, então ela não existe em tempo de
 * build e não pode ser achatada pelo `recortar-icones.mjs` como os esboços de encaixe foram. O
 * `brightness(0)` é a única forma de achatar uma imagem que o cliente só conhece em runtime.
 *
 * E a silhueta **troca o `alt` junto**: um leitor de tela que anunciasse o nome do peixe entregaria
 * de graça o que a silhueta existe para esconder.
 */
export default {
  name: 'FishIcon',
  props: {
    /** Ícone de interface: a classe `fa-*` sem o prefixo. Ex.: `atributo-sorte`, `nav-pescar`. */
    nome: { type: String, default: '' },
    /** Arte de conteúdo: a URL. Quando presente, vence o `nome`. */
    src: { type: String, default: '' },
    alt: { type: String, default: '' },
    tamanho: {
      type: String,
      default: 'medio',
      validator: (v) => ['miudo', 'pequeno', 'medio', 'grande', 'enorme'].includes(v),
    },
    /** Não descoberto: mostra a forma, esconde o desenho (D14). */
    silhueta: { type: Boolean, default: false },
    rotuloDaSilhueta: { type: String, default: 'Ainda não descoberto' },
    /** O que mostrar se a imagem falhar. Uma imagem quebrada é pior que um ícone genérico. */
    reserva: { type: String, default: '/images/ui/fish.png' },
  },
  emits: ['erro'],
  methods: {
    aoFalhar(evento) {
      if (evento.target.src.endsWith(this.reserva)) return; // a reserva também falhou; para aqui
      evento.target.src = this.reserva;
      this.$emit('erro');
    },
  },
};
</script>

<style scoped>
.fish-icon {
  display: inline-block;
  vertical-align: -0.125em;
  flex-shrink: 0;
  image-rendering: pixelated;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}

.fish-icon--arte {
  object-fit: contain;
}

/* Os tamanhos são múltiplos de 16 — a grade da DotGothic16 e a dos sprites. Um ícone de 32px numa
 * caixa de 30 é o mesmo borrão que o UI-11 aponta, só que menor. */
.fish-icon--miudo {
  width: 16px;
  height: 16px;
}
.fish-icon--pequeno {
  width: 24px;
  height: 24px;
}
.fish-icon--medio {
  width: 32px;
  height: 32px;
}
.fish-icon--grande {
  width: 48px;
  height: 48px;
}
.fish-icon--enorme {
  width: 64px;
  height: 64px;
}

/**
 * A silhueta: achata o desenho e deixa só a forma.
 *
 * `brightness(0)` zera a cor e `opacity` a levanta do fundo o suficiente para a forma ser lida sem
 * competir com o que está descoberto. O valor é baixo de propósito — uma silhueta forte demais
 * parece um item bloqueado, e o que ela deve parecer é uma vaga esperando ser preenchida.
 */
.fish-icon--silhueta {
  filter: brightness(0);
  opacity: 0.38;
}
</style>
